import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, Grid, InputLabel, MenuItem, Paper, Select, Stack, TablePagination, TextField, Typography,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import PageContainer from '../components/PageContainer';
import { useIsXs } from '../hooks/useMobileLayout';
import AppModuleScaffold from '../components/AppModuleScaffold';
import { ListCardField } from '../components/ListCardField';
import { listPayments, listSindicatos } from '../api/services';
import type { Payment, Sindicato } from '../../types/domain';
import { fmtCurrency, FEE_AGREEMENT_LABEL } from '../brand';
import { useAppSelector, useAppDispatch } from '../store/store';
import { showMessage } from '../store/uiSlice';

export default function AdminPayments() {
  const isXs = useIsXs();
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.auth.user);
  const isSuperAdmin = !!user?.isSuperAdmin;
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [items, setItems] = useState<Payment[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  /** SuperAdmin: filtrar por CAMARAS (tenantId + sindicatoId al API). null = todas. */
  const [filterCamaraScopeId, setFilterCamaraScopeId] = useState<number | null>(null);
  const [status, setStatus] = useState('');
  const [feeCode, setFeeCode] = useState('');
  const [doc, setDoc] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [camarasAll, setCamarasAll] = useState<Sindicato[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const scopeTenantId = useMemo(() => {
    if (!isSuperAdmin) return user?.tenantId ?? null;
    if (filterCamaraScopeId == null) return null;
    return camarasAll.find((c) => c.sindicatoId === filterCamaraScopeId)?.tenantId ?? null;
  }, [isSuperAdmin, user?.tenantId, filterCamaraScopeId, camarasAll]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number | undefined> = {
        page: page + 1,
        pageSize,
      };
      if (isSuperAdmin) {
        if (scopeTenantId != null) params.tenantId = scopeTenantId;
        if (filterCamaraScopeId != null) params.sindicatoId = filterCamaraScopeId;
      }
      if (status)  params.status = status;
      if (feeCode) params.feeCode = feeCode;
      if (doc)     params.documentNumber = doc;
      if (from)    params.from = new Date(from).toISOString();
      if (to)      params.to = new Date(to).toISOString();

      const res = await listPayments(params);
      setItems(res.items);
      setTotal(res.total);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudieron cargar los pagos');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, scopeTenantId, filterCamaraScopeId, status, feeCode, doc, from, to, isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    listSindicatos({ includeInactive: true })
      .then((r) => setCamarasAll(r.sindicatos))
      .catch(() => setCamarasAll([]));
  }, [isSuperAdmin]);

  useEffect(() => { load(); }, [load]);

  return (
    <PageContainer maxWidth="md" sx={{ pb: { xs: 10, md: 4 } }}>
      <AppModuleScaffold
        title="Historial de pagos"
        description="Consultá cobros simulados, estados y referencias de Mercado Pago de prueba. Podés filtrar por CAMARAS u organización, tipo, documento y fechas."
        actionLeft={(
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<FilterListIcon />}
            sx={{ borderRadius: 999, px: 2, fontWeight: 700, borderWidth: 2, textTransform: 'none' }}
            onClick={() => setFiltersOpen(true)}
          >
            Filtros
          </Button>
        )}
        actionRight={(
          <Box
            component="button"
            type="button"
            onClick={() => dispatch(showMessage({ message: 'Exportación a Excel: próximamente.', severity: 'info' }))}
            sx={{
              all: 'unset',
              cursor: 'pointer',
              color: 'primary.main',
              font: 'inherit',
              fontWeight: 800,
              fontSize: (t) => t.typography.body2.fontSize,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Exportar Excel (.xlsx)
          </Box>
        )}
      >
      <Dialog open={filtersOpen} onClose={() => setFiltersOpen(false)} maxWidth="sm" fullWidth fullScreen={isXs} scroll="body">
        <DialogTitle sx={{ fontWeight: 800 }}>Filtros</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {isSuperAdmin && (
              <FormControl size="small" fullWidth>
                <InputLabel>CAMARAS (alcance)</InputLabel>
                <Select
                  label="CAMARAS (alcance)"
                  value={filterCamaraScopeId ?? ''}
                  onChange={(e) => {
                    setPage(0);
                    setFilterCamaraScopeId(e.target.value === '' ? null : Number(e.target.value));
                  }}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {camarasAll.map((c) => (
                    <MenuItem key={c.sindicatoId} value={c.sindicatoId}>
                      {c.name}{c.tenantName ? ` — ${c.tenantName}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl size="small" fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select label="Estado" value={status} onChange={(e) => { setPage(0); setStatus(e.target.value); }}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="APPROVED">Aprobado</MenuItem>
                <MenuItem value="PENDING">Pendiente</MenuItem>
                <MenuItem value="REJECTED">Rechazado</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select label="Tipo" value={feeCode} onChange={(e) => { setPage(0); setFeeCode(e.target.value); }}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="AGREEMENT">{FEE_AGREEMENT_LABEL}</MenuItem>
                <MenuItem value="NO_AGREEMENT">Sin Acuerdo</MenuItem>
              </Select>
            </FormControl>
            <TextField size="small" fullWidth label="Documento" value={doc} onChange={(e) => { setPage(0); setDoc(e.target.value); }} />
            <TextField size="small" fullWidth label="Desde" type="datetime-local" value={from} onChange={(e) => { setPage(0); setFrom(e.target.value); }} InputLabelProps={{ shrink: true }} />
            <TextField size="small" fullWidth label="Hasta" type="datetime-local" value={to} onChange={(e) => { setPage(0); setTo(e.target.value); }} InputLabelProps={{ shrink: true }} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFiltersOpen(false)} variant="contained" fullWidth={isXs}>Listo</Button>
        </DialogActions>
      </Dialog>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ overflow: 'hidden', borderRadius: { xs: '12px', sm: 1 }, p: { xs: 1.5, sm: 2 } }}>
        {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box> : (
          <>
            {items.length ? (
              <Grid container spacing={2}>
                {items.map((p) => (
                  <Grid key={p.paymentId} size={{ xs: 12, sm: 6, lg: 4 }}>
                    <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, '&:last-child': { pb: 2 } }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                          <Typography variant="h6" sx={{ fontWeight: 800 }}>{fmtCurrency(p.amount, p.currency)}</Typography>
                          <Chip
                            size="small"
                            color={p.status === 'APPROVED' ? 'success' : p.status === 'PENDING' ? 'warning' : 'error'}
                            label={p.status}
                          />
                        </Stack>
                        <ListCardField label="Fecha">
                          <Typography variant="body2">{new Date(p.createdAt).toLocaleString('es-AR')}</Typography>
                        </ListCardField>
                        <ListCardField label="Organización">
                          <Typography variant="body2">{p.tenantName || `#${p.tenantId}`}</Typography>
                        </ListCardField>
                        <ListCardField label="CAMARAS">
                          <Typography variant="body2">{p.sindicatoName || (p.sindicatoId != null ? `#${p.sindicatoId}` : '—')}</Typography>
                        </ListCardField>
                        <ListCardField label="Tipo">
                          <Typography variant="body2">{p.feeCode === 'AGREEMENT' ? 'Acuerdo' : 'Sin Acuerdo'}</Typography>
                        </ListCardField>
                        <ListCardField label="Documento">
                          <Typography variant="body2">{p.documentType === 'DNI' ? 'DNI' : 'Pasaporte'} {p.documentNumber}</Typography>
                        </ListCardField>
                        <ListCardField label="Referencia">
                          <Typography variant="body2" component="code" sx={{ fontSize: '0.8rem', wordBreak: 'break-all' }}>{p.simulatedReference}</Typography>
                        </ListCardField>
                        <ListCardField label="Operador">
                          <Typography variant="body2">
                            {p.userFirstName ? `${p.userFirstName} ${p.userLastName ?? ''}`.trim() : (p.userDni || '—')}
                          </Typography>
                        </ListCardField>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                Sin pagos para los filtros aplicados.
              </Typography>
            )}
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Filas"
              sx={{ width: 1, overflow: 'auto', borderTop: 1, borderColor: 'divider', mt: 2, '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', rowGap: 1, px: 1 } }}
            />
          </>
        )}
      </Paper>
      </AppModuleScaffold>
    </PageContainer>
  );
}
